import { ref } from 'vue'
import { apiFailure } from '@/utils/plain-error'

const API_BASE = '/api/v1/auth'

const token = ref(localStorage.getItem('auth.token'))
const user = ref<any>(null)

export function useAuth() {

  async function register(data: {name: string; email: string; password: string; password_confirmation: string; phone?: string}) {
    const res = await fetch(`${API_BASE}/register`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify(data)
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    token.value = json.token
    user.value = json.user
    localStorage.setItem('auth.token', json.token)
    return json
  }

  async function login(data: {email: string; password: string}) {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify(data)
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    if (json.totp_required) return json
    token.value = json.token
    user.value = json.user
    localStorage.setItem('auth.token', json.token)
    return json
  }

  async function verifyTotp(userId: number, code: string) {
    const res = await fetch(`${API_BASE}/totp/verify`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({user_id: userId, code})
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    token.value = json.token
    user.value = json.user
    localStorage.setItem('auth.token', json.token)
    return json
  }

  async function fetchUser() {
    if (!token.value) return null
    const res = await fetch(`${API_BASE}/user`, {
      headers: {'Authorization': `Bearer ${token.value}`, 'Accept': 'application/json'}
    })
    if (!res.ok) { logout(); return null }
    const json = await res.json()
    user.value = json.data || json
    return user.value
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem('auth.token')
  }

  /**
   * Whether the stored token still opens a session: `invalid` only when the server says no, so a
   * network hiccup is never mistaken for a signed-out person.
   */
  async function checkToken(): Promise<'valid' | 'invalid' | 'unknown'> {
    const stored = localStorage.getItem('auth.token')
    if (!stored) return 'invalid'
    try {
      const res = await fetch(`${API_BASE}/user`, {
        headers: {'Authorization': `Bearer ${stored}`, 'Accept': 'application/json'}
      })
      if (res.ok) return 'valid'
      return res.status === 401 || res.status === 403 ? 'invalid' : 'unknown'
    } catch {
      return 'unknown'
    }
  }

  /** Sign out for real: the server forgets the token too, not only this browser. */
  async function signOut() {
    const stored = localStorage.getItem('auth.token')
    logout()
    if (!stored) return
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: {'Authorization': `Bearer ${stored}`, 'Accept': 'application/json'}
      })
    } catch {
      // The local session is already gone; an expired or unreachable token needs nothing more.
    }
  }

  async function updateProfile(data: { name: string; email: string; phone?: string; current_password?: string }) {
    const res = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token.value}`},
      body: JSON.stringify(data),
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    user.value = json.data || json
    return json
  }

  async function changePassword(data: { current_password?: string; password: string; password_confirmation: string }) {
    const res = await fetch(`${API_BASE}/password`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token.value}`},
      body: JSON.stringify(data),
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    return json
  }

  async function uploadAvatar(file: File) {
    const formData = new FormData()
    formData.append('avatar', file)
    const res = await fetch(`${API_BASE}/avatar`, {
      method: 'POST',
      headers: {'Authorization': `Bearer ${token.value}`, 'Accept': 'application/json'},
      body: formData,
    })
    const json = await res.json()
    if (!res.ok) throw apiFailure(res, json)
    user.value = json.data || json
    return json
  }

  return { token, user, register, login, verifyTotp, fetchUser, logout, signOut, checkToken, updateProfile, changePassword, uploadAvatar }
}
